import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { refresh } from '@descope/react-sdk';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import type { RootState } from '@/app/store';
import type { ClientContext } from '@/app/clientContext';
import { parseReleaseId } from '@/hooks/useReleaseId';
import { parseDeliveryIdentifier } from '@/hooks/useDeliveryIdentifier';

// PG&E and PGECONF share the same UI overrides (terminology, hidden priority rating, etc.)
const PGE_CLIENT_CODES = new Set(['PGE', 'PGECONF']);

export const getHeaders = (
  _token: string,
  apiIdentity?: string,
  headers: Record<string, string> = {}
) => {
  if (apiIdentity) {
    headers['X-Overstory-Email'] = apiIdentity;
  }

  // https://cloud.google.com/iap/docs/sessions-howto#ajax_requests
  headers['X-Requested-With'] = 'XMLHttpRequest';

  return headers;
};

export const prepareApiHeaders = (
  headers: Headers,
  { getState }: { getState: () => unknown }
) => {
  const apiIdentity = (getState() as RootState).login.user;
  if (apiIdentity) {
    headers.set('X-Overstory-Email', apiIdentity);
  }

  // https://cloud.google.com/iap/docs/sessions-howto#ajax_requests
  headers.set('X-Requested-With', 'XMLHttpRequest');

  return headers;
};

/**
 * Serializes request params for `fetchBaseQuery`. The default serializer
 * passes a plain object to `new URLSearchParams(obj)`, which stringifies
 * arrays by comma-joining (`status=draft,published`). FastAPI's list params
 * reject that: they expect repeated keys (`status=draft&status=published`).
 * This serializer emits one entry per array element.
 */
const repeatedKeyParamsSerializer = (
  params: Record<string, unknown>
): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === null || item === undefined) continue;
        search.append(key, String(item));
      }
    } else {
      search.append(key, String(value));
    }
  }
  return search.toString();
};

/**
 * URL query keys that are mirrored onto every outgoing request when present
 * in the page URL. Mirrors the backend `get_client_context` dependency,
 * which reads these once and attaches them to the request context.
 */
export const CONTEXT_URL_PARAMS = ['release', 'delivery', 'client'] as const;

/**
 * Collect the forwardable query params present in the current page URL,
 * skipping any key the caller has already set explicitly.
 */
const forwardedUrlParams = (
  exclude: Record<string, unknown> = {}
): Record<string, string> => {
  const urlParams = new URLSearchParams(window.location.search);
  const forwarded: Record<string, string> = {};
  for (const key of CONTEXT_URL_PARAMS) {
    if (key in exclude) continue;
    const value = urlParams.get(key);
    if (value !== null) forwarded[key] = value;
  }
  return forwarded;
};

const innerBaseQuery = fetchBaseQuery({
  prepareHeaders: prepareApiHeaders,
  credentials: 'include',
  paramsSerializer: repeatedKeyParamsSerializer,
});

let refreshInFlight: Promise<boolean> | undefined;
let lastRefresh: Promise<boolean> | undefined;

const recoverSession = () => {
  if (!refreshInFlight) {
    refreshInFlight = Promise.resolve()
      .then(() => refresh())
      .then((result) => result?.ok === true)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = undefined;
      });
    lastRefresh = refreshInFlight;
  }
  return refreshInFlight;
};

export const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const fetchArgs: FetchArgs = typeof args === 'string' ? { url: args } : args;
  const callerParams = (fetchArgs.params ?? {}) as Record<string, unknown>;

  const merged: Record<string, unknown> = {
    ...callerParams,
    ...forwardedUrlParams(callerParams),
  };

  const nextArgs: FetchArgs =
    Object.keys(merged).length > 0
      ? { ...fetchArgs, params: merged }
      : fetchArgs;
  await refreshInFlight;
  const previousRefresh = lastRefresh;
  const result = await innerBaseQuery(nextArgs, api, extraOptions);
  if (result.error?.status === 401) {
    if (api.signal.aborted) return result;

    // Refresh the Descope session before retrying once. Reuse a concurrent
    // refresh if another request already started recovery while this response was in flight.
    const recovered = await (lastRefresh !== previousRefresh
      ? lastRefresh
      : recoverSession());
    if (recovered && !api.signal.aborted) {
      return innerBaseQuery(nextArgs, api, extraOptions);
    }
  }
  return result;
};

export const isPge = (context: ClientContext) => {
  return PGE_CLIENT_CODES.has(context.code.toUpperCase());
};

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

type ParamValue = string | number | boolean | null | undefined;

export const addMvtCachingParams = (
  params: Record<string, ParamValue>,
  mvtUrl: string
) => {
  const deliveryIdentifier = parseDeliveryIdentifier(window.location.search);
  if (deliveryIdentifier) {
    params.delivery = deliveryIdentifier;
  }

  const metadata = sessionStorage.getItem('metadata') ?? null;
  const clientCode = metadata ? JSON.parse(metadata).code : '';

  if (clientCode !== '') {
    params.client_code = clientCode;
  }

  const releaseId = parseReleaseId(window.location.search);
  if (releaseId) {
    params.release = releaseId;
  }

  mvtUrl = addParamsToUrl(params, mvtUrl);

  return mvtUrl;
};

export const addParamsToUrl = (
  params: Record<string, ParamValue>,
  url: string
) => {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (value === '' || value === false) continue;
    const encodedKey = encodeURIComponent(key);
    parts.push(`${encodedKey}=${encodeURIComponent(value)}`);
  }

  if (parts.length === 0) return url;

  const separator = url.includes('?') ? '&' : '?';
  return url + separator + parts.join('&');
};

/**
 * Append the forwardable query params from the page URL (release, delivery) to
 * `url`, so plain `fetch` calls honor the same request context as RTK Query's
 * baseQueryWithAuth. No-op when none are present.
 */
export const withForwardedParams = (url: string): string =>
  addParamsToUrl(forwardedUrlParams(), url);

/**
 * RTK Query errors can be either a `FetchBaseQueryError` (has a numeric `status`)
 * or a `SerializedError` (JS exception, no `status`). Use this to safely check
 * the HTTP status code without manual type narrowing at each call site.
 */
export const isHttpError = (
  error: unknown,
  status: number | number[]
): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  (Array.isArray(status)
    ? status.includes(error.status as number)
    : error.status === status);
